import type { AdminOrderPaymentCaptureDto, PaymentCapturePhase } from '@carcheck/shared';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LockClockIcon from '@mui/icons-material/LockClock';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import SyncIcon from '@mui/icons-material/Sync';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { describeCaptureStory, type CaptureStoryText } from './captureStory';
import { tokens } from '../../design';

/**
 * AUT-309 — «что уже пробовали с этим холдом и что будет дальше».
 *
 * The block that answers the question the Платёж tab could not: a hold on
 * `authorized` used to look the same whether nobody had touched it, a retry was
 * queued, or the automation had given up. See `./captureStory.ts` for the
 * incident and for every rule about WHAT is said; this file only paints it.
 *
 * The one rule that lives here rather than there: **`retry_scheduled` must not
 * render as an `Alert`.** MUI's `Alert` carries `role="alert"`, which is both an
 * assistive-tech interruption and — more to the point — the visual grammar the
 * rest of this tab uses for "something is wrong". A scheduled automatic retry is
 * the automation working, and dressing it as a problem is what produced the
 * original misreading. Quiet phases get a bordered card and a muted icon;
 * `needs_operator` alone gets the `Alert`.
 */
const PHASE_ICON: Record<PaymentCapturePhase, typeof SyncIcon | null> = {
  not_applicable: null,
  awaiting_trigger: LockClockIcon,
  in_flight: SyncIcon,
  retry_scheduled: ScheduleSendIcon,
  needs_operator: HourglassEmptyIcon,
};

const Line = ({ text }: { text: CaptureStoryText }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Typography
      component="p"
      sx={{ fontSize: 12, lineHeight: 1.45, color: 'inherit', m: 0 }}
      data-testid="capture-story-line"
    >
      {t(text.key, { ...text.values })}
    </Typography>
  );
};

export const PaymentCaptureStory = ({
  capture,
  /** Injectable clock so the elapsed-lower-bound branch is spec-testable. */
  now,
}: {
  capture: AdminOrderPaymentCaptureDto;
  now?: number;
}): ReactElement | null => {
  const { t } = useTranslation();
  const story = describeCaptureStory(capture, now);
  if (story === null) return null;

  const Icon = PHASE_ICON[story.phase];
  const headline = t(story.headline.key, { ...story.headline.values });
  const probe = {
    'data-testid': 'capture-story',
    'data-phase': story.phase,
    'data-tone': story.tone,
  };

  if (story.tone === 'attention' && story.severity !== null) {
    return (
      <Alert severity={story.severity} sx={{ py: 0.5 }} {...probe}>
        <AlertTitle sx={{ fontSize: 13, fontWeight: 700, mb: 0.25 }}>{headline}</AlertTitle>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
          {story.lines.map((line) => (
            <Line key={line.key} text={line} />
          ))}
          {/*
           * The raw machine code, verbatim and on purpose. `blockedReason` shares
           * its nine-value vocabulary with the `[hold.sweep]` log lines precisely
           * so that an operator reading this drawer and an engineer grepping the
           * log are talking about the same row — that only pays off if the token
           * is actually quotable off the screen. It is the ONE place a raw code is
           * shown, next to (never instead of) the Russian sentence above.
           */}
          {capture.blockedReason !== null ? (
            <Box
              component="code"
              data-testid="capture-story-code"
              title={t('orders.capture.story.codeHint')}
              sx={{ fontSize: 10.5, opacity: 0.7, fontFamily: 'monospace', mt: 0.25 }}
            >
              {capture.blockedReason}
            </Box>
          ) : null}
        </Box>
      </Alert>
    );
  }

  return (
    <Box
      {...probe}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        px: 1.5,
        py: 1.25,
        color: tokens.text.secondary,
        bgcolor: 'background.paper',
      }}
    >
      {Icon !== null ? <Icon sx={{ fontSize: 15, mt: '1px', flexShrink: 0 }} /> : null}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, minWidth: 0 }}>
        <Typography
          component="p"
          sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.4, color: 'text.primary', m: 0 }}
        >
          {headline}
        </Typography>
        {story.lines.map((line) => (
          <Line key={line.key} text={line} />
        ))}
      </Box>
    </Box>
  );
};
